import axios from 'axios';
import { attachSnapStoreAdapter } from './snapStoreApi';

// In produzione, usa URL relativo se REACT_APP_API_URL non è impostato
// In sviluppo, usa localhost:8080
const getBaseURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In produzione (quando non siamo su localhost:3000), usa URL relativo
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return '/api/v1';
  }
  // In sviluppo, backend su localhost:8080 con prefisso /api/v1
  return 'http://localhost:8080/api/v1';
};

export { getBaseURL };

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

attachSnapStoreAdapter(api);

/**
 * Endpoint dove 401/403 è una risposta normale (credenziali errate, ecc.),
 * non una sessione scaduta: non si deve fare refresh né redirect full-page.
 */
function isPublicAuthRequest(config) {
  const url = config?.url || '';
  return (
    url.includes('auth/login')
    || url.includes('auth/register')
    || url.includes('auth/forgot-password')
    || url.includes('auth/reset-password')
  );
}

// Interceptor per aggiungere token a tutte le richieste
api.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    // Endpoint pubblici: niente Bearer (evita edge-case JWT su route permitAll)
    const isPublic =
      url.includes('/public/')
      || url.startsWith('public/')
      || url.includes('/seo/')
      || url.startsWith('seo/');
    if (!isPublic) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor per gestire errori di autenticazione
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

// Controlla se il token JWT è scaduto
const isTokenExpired = () => {
  const token = localStorage.getItem('token');
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Login / registrazione / recupero password: lasciare che la UI gestisca l'errore (no window.location)
    if (originalRequest && (status === 401 || status === 403) && isPublicAuthRequest(originalRequest)) {
      return Promise.reject(error);
    }

    // Gestisci 401 (non autenticato) — prova a fare refresh
    if (status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const res = await axios.post(`${getBaseURL()}/auth/refresh`, { refreshToken });
          const newToken = res.data.token ?? res.data.accessToken;
          if (newToken) {
            localStorage.setItem('token', newToken);
            if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            isRefreshing = false;
            return api(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;
        }
      }

      // Refresh fallito o assente: logout e redirect
      isRefreshing = false;
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Gestisci 403 con token scaduto: il token è presente ma scaduto → prova refresh
    if (status === 403 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh') && isTokenExpired()) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const res = await axios.post(`${getBaseURL()}/auth/refresh`, { refreshToken });
          const newToken = res.data.token ?? res.data.accessToken;
          if (newToken) {
            localStorage.setItem('token', newToken);
            if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            isRefreshing = false;
            return api(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;
        }
      }

      // Refresh fallito: logout
      isRefreshing = false;
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

/**
 * Messaggio utente da errori Axios (ErrorResponse Spring, validazione, rete).
 */
export function getApiErrorMessage(error, fallback = 'Si è verificato un errore') {
  const data = error?.response?.data;
  if (data == null) {
  if (!error?.response && error?.message === 'Network Error') {
    return 'Impossibile contattare il server. Controlla la connessione.';
  }
  if (error?.code === 'ECONNABORTED') {
    return 'Operazione scaduta per timeout. Riprova tra qualche secondo.';
  }
    return fallback;
  }
  if (typeof data === 'string') return data;
  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  if (Array.isArray(data.details) && data.details.length) return data.details.join('. ');
  return fallback;
}

export default api;

